import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useModal } from 'mui-modal-provider';
import { useState } from 'react';
import { SelectElement, TextFieldElement, useFormState, useWatch } from 'react-hook-form-mui';
import { makeStyles } from 'tss-react/mui';
import useDebounce from '../../../hooks/useDebounce';
import { useAvailableNumbers, useChangeSicNumber } from '../../../sic-lib/api/hooks/useTwilio';
import { AvailableNumbersRequest } from '../../../sic-lib/api/twilio/types/AvailableNumbersRequest';
import { AvailableNumber } from '../../../sic-lib/types/AvailableNumber';
import { useAppDispatch } from '../../../store/hooks';
import { requestConfig } from '../../../store/users';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../Shared/ButtonLoading';
import DialogConfirmAction from '../../Shared/DialogConfirmAction/DialogConfirmAction';
import Loader from '../../Shared/Loader';

type Props = {};

const countries = [
    {
        id: 'US',
        label: 'United States',
    },
    {
        id: 'CA',
        label: 'Canada',
    },
];

export function AvailableNumberFormControls({}: Props) {
    const { showModal } = useModal();
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    const [selectedNumber, setSelectedNumber] = useState<AvailableNumber | undefined>(undefined);
    const { isValid } = useFormState();
    const formValues = useWatch() as AvailableNumbersRequest;
    const debouncedFormValues = useDebounce(formValues, 500);
    const {
        data: availableNumbers,
        isFetching,
        error,
    } = useAvailableNumbers(debouncedFormValues, isValid);
    const {
        isLoading: isChangingNumber,
        isError: isChangeNumberError,
        isSuccess: isChangeNumberSuccess,
        mutateAsync: changeSicNumber,
    } = useChangeSicNumber();

    const handleBtnGetNumber = () => {
        const modal = showModal(DialogConfirmAction, {
            title: 'Get Selected Number',
            description: `Are you sure you want to replace your current StayInContact phone number with ${selectedNumber?.friendlyName}? This number will be released and anyone can acquire it.`,
            onConfirm: async () => {
                if (!selectedNumber?.number) return;
                await changeSicNumber(selectedNumber.number);
                dispatch(requestConfig());
            },
            onCancel: () => {
                modal.hide();
            },
        });
    };

    return (
        <>
            <Grid container item spacing={2}>
                <Grid item sm={6}>
                    <Card>
                        <CardHeader title="Search for a number" />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <SelectElement
                                        name="country"
                                        label="Country"
                                        options={countries}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextFieldElement
                                        name="areaCode"
                                        label="Area Code"
                                        placeholder="Number starts with"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextFieldElement
                                        name="numberContains"
                                        label="Number Contains"
                                        fullWidth
                                        validation={{
                                            required: false,
                                            minLength: 2,
                                            maxLength: 7,
                                            pattern: {
                                                value: /^\d{0,7}$/,
                                                message: 'Can only contain numbers',
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextFieldElement
                                        name="stateProvince"
                                        label="State/Province (FL, TX, ON, etc)"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextFieldElement
                                        name="locality"
                                        label="Locality (Town, County, Municipality, etc)"
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                            <AxiosErrorAlert error={error} sx={{ mt: 2 }} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item sm={6}>
                    <Card>
                        <CardHeader title="Select a number to acquire" />
                        <Divider />
                        <List className={classes.phoneList}>
                            <Loader isLoading={isFetching}>
                                {availableNumbers?.map(number => (
                                    <ListItem
                                        key={number.number}
                                        disablePadding
                                        secondaryAction={
                                            selectedNumber?.number === number.number && (
                                                <IconButton edge="end">
                                                    <CheckCircleIcon />
                                                </IconButton>
                                            )
                                        }
                                    >
                                        <ListItemButton
                                            onClick={() => setSelectedNumber(number)}
                                            selected={selectedNumber?.number === number.number}
                                        >
                                            <ListItemText
                                                primary={number.friendlyName}
                                                secondary={
                                                    (number.locality || '') +
                                                    ' ' +
                                                    (number.region || '')
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                                {!availableNumbers?.length && (
                                    <Typography variant="caption" className={classes.noResults}>
                                        No numbers found. Try a different search.
                                    </Typography>
                                )}
                            </Loader>
                        </List>
                        <Divider />
                        <CardActions>
                            <Typography>
                                {selectedNumber?.friendlyName || 'No number selected'}
                            </Typography>
                            <ButtonLoading
                                variant="contained"
                                disabled={!selectedNumber}
                                onClick={handleBtnGetNumber}
                                isLoading={isChangingNumber}
                                isError={isChangeNumberError}
                                isSuccess={isChangeNumberSuccess}
                            >
                                Get Selected Number
                            </ButtonLoading>
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    list: {
        padding: theme.spacing(2),
    },
    phonePaper: {
        width: '100%',
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(2),
    },
    phoneList: {
        overflowY: 'scroll',
        height: '314px',
        width: '100%',
        padding: 0,
    },
    noResults: {
        display: 'block',
        textAlign: 'center',
        padding: theme.spacing(4),
    },
}));
